import { DEFAULT_CONFIG } from './config.js';
import { toSnapshotStruct } from '../utils/utils.js';
import { BASIC_ROOM_SETTINGS } from '../../../shared/game-balance.js';

const model = {
  config: { ...DEFAULT_CONFIG },
  state: {
    serverNow: Date.now(),
    tick: 0,
    players: [],
    status: {
      gameId: '',
      roundNumber: 0,
      running: false,
      waitingNext: false,
      gameOver: false,
      paused: false,
      nextRoundAt: 0,
      matchRestartAt: 0,
      roundEndsAt: 0,
      roundStartFreezeUntilMs: 0,
      timeLeftAtEndMs: 0
    },
    powerups: []
  }
};

const authoritative = {
  state: toSnapshotStruct(model.state, null, { includeEvents: true })
};

const ui = {
  serverClockOffsetMs: 0,
  clockSynced: false,
  waitingRoundKey: '',
  waitingOverlayNeedsAnimate: false,
  matchEndOverlayShowAt: 0,
  matchEndOverlayReady: false,
  targetCountdownEndsAt: 0,
  targetCountdownSeconds: -1,
  lastOverlayCountdown: -1,
  overlayStateSig: '',
  scoreSig: '',
  scoreOrderSig: '',
  standingsSig: '',
  controlsSig: '',
  hasStateSnapshot: false,
  powerupIds: new Set(),
  powerupIdsScratch: new Set(),
  powerupsVersion: null,
  pendingStartSfx: false,
  scheduledStartSfxAt: 0,
  suppressStartSfxUntil: 0,
  overlayDirty: true,
  scoreDirty: true,
  controlsDirty: true,
  lastFrameMs: 0,
  smoothDeltaMs: 16,
  scoreRows: new Map(),
  // Team header rows in the standings list, cached like the player rows.
  scoreTeamRows: new Map(),
  lobbyRoomsSig: '',
  lobbyPlayersSig: ''
};

const keyState = {
  left: false,
  right: false
};

const keyIntent = {
  leftDown: false,
  rightDown: false,
  lastPressedDir: 0
};

const net = {
  connected: false,
  roomId: 'default',
  playerName: `P${Math.floor(Math.random() * 1000)}`,
  localPlayerId: null,
  lastAckSeq: null,
  lastSentInputSig: '',
  lastSnapshotTick: 0,
  lastSnapshotAtMs: 0,
  pendingInputs: [],
  confirmedInput: { left: false, right: false, seq: 0 },
  predictionBaseInput: { left: false, right: false },
  predictionInputHistory: [],
  snapshotBuffer: [],
  pingSeq: 1,
  pingSentAt: new Map(),
  rttMs: 0,
  jitterMs: 0,
  turnRadiusPercent: BASIC_ROOM_SETTINGS.turnRadius,
  turnRadius: DEFAULT_CONFIG.turnRadius,
  // Server-measured WebSocket round trips for the live match, keyed by engine
  // player id. This is shared status, unlike the local rttMs above.
  playerNetworkStats: new Map(),
  correctionPx: 0,
  correctionAngle: 0,
  correctionHard: false,
  predictionAnchor: null,
  predictionDisplay: null,
  predictionCorrection: { x: 0, y: 0, angle: 0, lastAtMs: 0 },
  predictedInput: { left: false, right: false },
  lastInputSendClientMs: 0,
  lastControlChangeClientMs: 0,
  debugStats: {
    snapshotGapMs: 0,
    snapshotGapMaxMs: 0,
    predictionBridgeMs: 0,
    predictionBridgeMaxMs: 0,
    correctionPx: 0,
    correctionPxMax: 0,
    correctionAngle: 0,
    correctionAngleMax: 0,
    hardCorrections: 0,
    snapshotBufferHighWater: 0,
    pingHighWater: 0,
    pendingInputHighWater: 0,
    predictionHistoryHighWater: 0,
    predictionHistoryOverflows: 0
  }
};

const authState = {
  startedNetworking: false,
  busy: false,
  authenticated: false,
  // True when the player entered without a passkey session (guest mode).
  guest: false
};

const lobby = {
  mode: 'browser',
  visible: false,
  roomId: 'default',
  roomName: 'Default',
  connectedPlayers: 0,
  maxPlayers: 8,
  // Team mode is a room setting, not a count preset: see MATCH_FORMATS.
  teamMode: false,
  // 1, matching the FFA preset the lobby opens on: this seeds the create
  // form, so a 2 here silently overrode the form's own default.
  minPlayersToStart: 1,
  startMode: 'host',
  hostName: '',
  isHost: false,
  connectedClients: 0,
  humanPlayers: 0,
  botPlayers: 0,
  manualBotCount: 0,
  phase: 'waiting',
  canStart: false,
  availableRooms: [],
  players: [],
  spectators: [],
  iAmSpectator: false,
  iAmMuted: false,
  // Leaderboard placings for the last finished match, [{ id, rank }] by engine
  // id. Bots never appear here, and neither does a match the leaderboard
  // policy refused to record.
  matchBoardRanks: [],
  matchBoardSize: 0,
  // Start countdown, mapped to the local clock from the room_state that
  // announced it; 0 = no countdown running.
  startCountdownLocalEndsAt: 0,
  startCountdownStepMs: 1000,
  countdownStopsLeft: 3,
  message: 'Joining room...'
};

const lobbyRuntime = {
  // True once the player has edited the create row, which stops renderLobby
  // reseeding it from the last room's settings on every pushed update.
  createFormTouched: false,
  returnToLobbyInProgress: false,
  botActionInFlight: false,
  copyButtonResetHandle: 0,
  // True while a connected player peeks at the waiting room mid-match; the
  // running match keeps going and the spec/play toggle returns them to it.
  matchLobbyPeek: false
};

export {
  model,
  authoritative,
  ui,
  keyState,
  keyIntent,
  net,
  authState,
  lobby,
  lobbyRuntime
};
